// Video screen (the `video` step). Browser twin of the desktop VideoPlayerPage.
// Plays a researcher-configured video - an uploaded file (served as a signed GCS
// URL) or a direct video-file URL - in a native <video> element, streamed
// straight from storage (no byte-proxy; range requests handle seeking).
//
// Parity with video_player_page.py:
//   - source: signed/direct URL fetched over the session
//   - autoplay vs click-to-play
//   - controls: none by default; only the enabled ones are shown; play-through
//     force-disables the seek bar + forward-skip
//   - end condition: playthrough (ends on video end) / skippable (Continue
//     always) / timed (auto-continue after N s, optional countdown)
//   - graceful failure: dead/unplayable source -> message + Continue
//   - every interaction (start/play/pause/seek/volume/replay/end) is captured
//     and submitted in the result; the server logs one row each.
//
// NOT yet ported (desktop-only for now): mid-video survey triggers.
//
// Submit payload: VideoResult (source metadata, watch time, end_trigger, events).

import { useEffect, useRef, useState } from "react";
import type { WebRendererProps } from "./registry";
import type {
  VideoContent,
  VideoEvent,
  VideoResult,
  DocMidBlockSurvey,
  VideoSurveyResponse,
} from "../api/types";
import { fetchVideo } from "../session/sessionDriver";
import { SurveyForm, type SurveyResponse } from "./survey/SurveyForm";

const SKIP_STEP_MS = 10_000;

export function formatRemaining(totalSeconds: number): string {
  const remaining = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")} remaining`;
}

type Phase = "loading" | "playing" | "error";

function bool(v: unknown): boolean {
  return v === true;
}

export default function VideoRenderer({ step, session, onDone }: WebRendererProps) {
  const settings = (step.settings ?? {}) as Record<string, unknown>;
  const title = typeof settings.title === "string" ? settings.title : "";
  const autoplay = settings.autoplay !== false; // default true
  const controls = (settings.controls ?? {}) as Record<string, unknown>;
  const endCondition = (settings.end_condition as string) ?? "playthrough";
  const durationSeconds =
    typeof settings.duration_seconds === "number" ? settings.duration_seconds : 60;
  const showCountdown = bool(settings.show_countdown) && endCondition === "timed";
  const pauseAfter =
    typeof settings.pause_after_seconds === "number" ? settings.pause_after_seconds : 0;

  const isPlaythrough = endCondition === "playthrough";
  // Play-through suppresses free scrubbing (the seek bar). A skip-forward toggle
  // is honored even under play-through (Jacob, 2026-07-10): if the researcher
  // enabled the button, it shows and works.
  const showSeek = bool(controls.seek) && !isPlaythrough;
  const showSkipForward = bool(controls.skip_forward);
  const showPlayPause = bool(controls.play_pause);
  const showSkipBack = bool(controls.skip_back);
  const showVolume = bool(controls.volume);
  const showReplay = bool(controls.replay);
  const anyControls =
    showPlayPause || showSeek || showSkipForward || showSkipBack || showVolume || showReplay;

  const [phase, setPhase] = useState<Phase>("loading");
  const [errorMsg, setErrorMsg] = useState("Loading video...");
  const [needsClick, setNeedsClick] = useState(!autoplay);
  const [isPlaying, setIsPlaying] = useState(false);
  const [positionMs, setPositionMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [volume, setVolume] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(
    showCountdown ? durationSeconds : null,
  );
  const [activeSurvey, setActiveSurvey] = useState<DocMidBlockSurvey | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const metaRef = useRef<VideoContent | null>(null);
  const startRef = useRef<number>(Date.now());
  const lockedRef = useRef(false);
  const startedRef = useRef(false);
  const eventsRef = useRef<VideoEvent[]>([]);
  const watchMsRef = useRef(0);
  const playStartRef = useRef<number | null>(null);
  const timersRef = useRef<number[]>([]);
  // Mid-video survey state: triggers sorted by time, fired-once tracking, and the
  // in-flight survey that gates playback + defers any end until it closes.
  const triggersRef = useRef<DocMidBlockSurvey[]>([]);
  const firedRef = useRef<Set<number>>(new Set());
  const awaitingSurveyRef = useRef(false);
  const pausedForSurveyRef = useRef(false);
  const pendingEndRef = useRef<VideoResult["end_trigger"] | null>(null);
  const surveyResponsesRef = useRef<VideoSurveyResponse[]>([]);
  const activeSurveyRef = useRef<DocMidBlockSurvey | null>(null);

  function log(event: string, extra: Record<string, unknown> = {}) {
    const v = videoRef.current;
    eventsRef.current.push({
      event,
      at_seconds: Math.round(((Date.now() - startRef.current) / 1000) * 1000) / 1000,
      position_ms: v ? Math.round(v.currentTime * 1000) : 0,
      ...extra,
    });
  }

  function accrueWatch() {
    if (playStartRef.current !== null) {
      watchMsRef.current += Date.now() - playStartRef.current;
      playStartRef.current = null;
    }
  }

  function safePlay() {
    const v = videoRef.current;
    if (!v) return;
    try {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch {
      /* autoplay blocked, or no media backend (jsdom): ignore */
    }
  }

  function safePause() {
    try {
      videoRef.current?.pause();
    } catch {
      /* no media backend (jsdom): ignore */
    }
  }

  function startPlay() {
    if (!startedRef.current) {
      startedRef.current = true;
      log("video_started");
    }
    setNeedsClick(false);
    safePlay();
  }

  function complete(endTrigger: VideoResult["end_trigger"]) {
    // Never end out from under an open mid-video survey; honor it on close.
    if (awaitingSurveyRef.current) {
      pendingEndRef.current = endTrigger;
      return;
    }
    if (lockedRef.current) return; // re-entrance guard
    lockedRef.current = true;
    accrueWatch();
    timersRef.current.forEach((t) => window.clearInterval(t));
    timersRef.current.forEach((t) => window.clearTimeout(t));
    const v = videoRef.current;
    safePause();
    if (endTrigger === "skipped") setSubmitted(true);

    const meta = metaRef.current;
    const result: VideoResult = {
      source_type: meta?.source_type ?? "",
      video_media_id: meta?.video_media_id ?? null,
      video_url: meta?.source_type === "url" ? meta?.url ?? "" : "",
      duration_ms: v ? Math.round((v.duration || 0) * 1000) : 0,
      watch_time_seconds: Math.round((watchMsRef.current / 1000) * 1000) / 1000,
      time_on_block_seconds:
        Math.round(((Date.now() - startRef.current) / 1000) * 1000) / 1000,
      end_trigger: endTrigger,
      events: eventsRef.current,
      survey_responses: surveyResponsesRef.current,
    };
    const submit = () => onDone({ kind: "submit", result });
    if (pauseAfter > 0) window.setTimeout(submit, pauseAfter * 1000);
    else submit();
  }

  function showError(message: string) {
    setPhase("error");
    setErrorMsg(message);
  }

  // Load the source.
  useEffect(() => {
    let alive = true;
    startRef.current = Date.now();
    (async () => {
      try {
        const meta = await fetchVideo(session);
        if (!alive) return;
        metaRef.current = meta;
        triggersRef.current = [...(meta.mid_block_surveys ?? [])].sort(
          (a, b) => a.value - b.value,
        );
        if (!meta.url) {
          showError("This video is not available. Click Continue to proceed.");
          return;
        }
        setPhase("playing");
      } catch {
        if (alive) showError("The video could not be loaded. Click Continue to proceed.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [session]);

  // Timers: timed auto-end + countdown. Armed once we are playing.
  useEffect(() => {
    if (phase !== "playing") return;
    if (endCondition === "timed") {
      const end = window.setTimeout(() => complete("timed_auto"), durationSeconds * 1000);
      timersRef.current.push(end);
      if (showCountdown) {
        const tick = window.setInterval(() => {
          const left = durationSeconds - (Date.now() - startRef.current) / 1000;
          setRemaining(Math.max(0, left));
        }, 1000);
        timersRef.current.push(tick);
      }
    }
    return () => {
      timersRef.current.forEach((t) => window.clearInterval(t));
      timersRef.current.forEach((t) => window.clearTimeout(t));
      timersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // --- <video> element event handlers ---
  function onLoadedMetadata() {
    const v = videoRef.current;
    if (v) setDurationMs(Math.round((v.duration || 0) * 1000));
    if (autoplay) startPlay();
  }
  function onPlay() {
    playStartRef.current = Date.now();
    setIsPlaying(true);
    log("video_play");
  }
  function onPause() {
    accrueWatch();
    setIsPlaying(false);
    // A pause fired by reaching the end is handled by onEnded; ignore the
    // implicit pause there.
    const v = videoRef.current;
    if (v && !v.ended) log("video_pause");
  }
  function onTimeUpdate() {
    const v = videoRef.current;
    if (v) {
      const posMs = Math.round(v.currentTime * 1000);
      setPositionMs(posMs);
      checkSurveyTriggers(posMs);
    }
  }
  function onEnded() {
    accrueWatch();
    log("video_ended");
    if (endCondition === "playthrough" || endCondition === "skippable") {
      complete("finished");
    }
  }
  function onError() {
    showError("This video could not be played. Click Continue to proceed.");
  }

  // --- mid-video survey triggers ---
  // Fire the earliest not-yet-fired trigger the playback position has reached,
  // one at a time. Pausing (default) holds the video behind the survey overlay.
  function checkSurveyTriggers(positionMs: number) {
    if (awaitingSurveyRef.current || lockedRef.current) return;
    const list = triggersRef.current;
    for (let i = 0; i < list.length; i++) {
      if (firedRef.current.has(i)) continue;
      if (positionMs >= list[i].value * 1000) {
        firedRef.current.add(i);
        fireSurvey(list[i]);
        return;
      }
      break; // value-sorted: the first unreached trigger ends the scan
    }
  }

  function fireSurvey(trigger: DocMidBlockSurvey) {
    awaitingSurveyRef.current = true;
    pausedForSurveyRef.current = trigger.pause !== false;
    if (pausedForSurveyRef.current) safePause();
    log("video_survey_shown", { survey_id: trigger.survey_id });
    activeSurveyRef.current = trigger;
    setActiveSurvey(trigger);
  }

  function onSurveySubmit(responses: SurveyResponse[]) {
    const trigger = activeSurveyRef.current;
    if (trigger) {
      surveyResponsesRef.current.push({
        survey_id: trigger.survey_id,
        survey_name: trigger.survey_name,
        responses,
      });
    }
    log("video_survey_done");
    activeSurveyRef.current = null;
    setActiveSurvey(null);
    awaitingSurveyRef.current = false;

    // An end condition that came due while the survey was open was deferred;
    // honor it now instead of resuming.
    if (pendingEndRef.current) {
      const t = pendingEndRef.current;
      pendingEndRef.current = null;
      pausedForSurveyRef.current = false;
      complete(t);
      return;
    }
    if (pausedForSurveyRef.current) {
      pausedForSurveyRef.current = false;
      safePlay();
    }
  }

  // --- control handlers ---
  function togglePlay() {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) startPlay();
    else safePause();
  }
  function doSeek(newMs: number) {
    const v = videoRef.current;
    if (!v) return;
    log("video_seek", { position_ms: newMs, from_ms: Math.round(v.currentTime * 1000) });
    v.currentTime = newMs / 1000;
    setPositionMs(newMs);
  }
  function skip(deltaMs: number) {
    const v = videoRef.current;
    if (!v) return;
    const dur = (v.duration || 0) * 1000;
    const next = Math.max(0, Math.min((dur || Infinity), v.currentTime * 1000 + deltaMs));
    doSeek(next);
  }
  function replay() {
    const v = videoRef.current;
    if (!v) return;
    log("video_replay", { position_ms: 0 });
    v.currentTime = 0;
    startPlay();
  }
  function changeVolume(value: number) {
    const v = videoRef.current;
    setVolume(value);
    if (v) v.volume = value;
    log("video_volume", { volume: Math.round(value * 100) });
  }

  return (
    <div className="jol-vid">
      {title.trim() ? <div className="jol-vid-title">{title}</div> : null}

      {phase === "error" ? (
        <div className="jol-vid-stage">
          <div className="jol-vid-error">{errorMsg}</div>
        </div>
      ) : (
        <div className="jol-vid-stage">
          <video
            ref={videoRef}
            className="jol-vid-video"
            src={metaRef.current?.url}
            playsInline
            onLoadedMetadata={onLoadedMetadata}
            onPlay={onPlay}
            onPause={onPause}
            onTimeUpdate={onTimeUpdate}
            onEnded={onEnded}
            onError={onError}
          />
          {phase === "loading" ? (
            <div className="jol-vid-loading">Loading video...</div>
          ) : null}
          {phase === "playing" && needsClick && !activeSurvey ? (
            <button type="button" className="jol-vid-bigplay" onClick={startPlay}>
              &#9654; Play
            </button>
          ) : null}
          {activeSurvey ? (
            <div className="jol-vid-survey-overlay">
              {/* Render the survey's own card (the same one the standalone
                  survey preview shows) directly on the overlay - no wrapping
                  card, so there is no card-in-card. */}
              <SurveyForm
                questions={activeSurvey.questions}
                submitLabel="Submit"
                onSubmit={onSurveySubmit}
              />
            </div>
          ) : null}
        </div>
      )}

      {phase === "playing" && anyControls ? (
        <div className="jol-vid-controls">
          {showPlayPause ? (
            <button type="button" className="jol-vid-ctrl" onClick={togglePlay}>
              {isPlaying ? "Pause" : "Play"}
            </button>
          ) : null}
          {showSkipBack ? (
            <button type="button" className="jol-vid-ctrl" onClick={() => skip(-SKIP_STEP_MS)}>
              &laquo; 10s
            </button>
          ) : null}
          {showSkipForward ? (
            <button type="button" className="jol-vid-ctrl" onClick={() => skip(SKIP_STEP_MS)}>
              10s &raquo;
            </button>
          ) : null}
          {showSeek ? (
            <input
              type="range"
              className="jol-vid-seek"
              min={0}
              max={durationMs || 0}
              value={positionMs}
              onChange={(e) => doSeek(Number(e.target.value))}
              aria-label="Seek"
            />
          ) : null}
          {showReplay ? (
            <button type="button" className="jol-vid-ctrl" onClick={replay}>
              Replay
            </button>
          ) : null}
          {showVolume ? (
            <input
              type="range"
              className="jol-vid-volume"
              min={0}
              max={100}
              value={Math.round(volume * 100)}
              onChange={(e) => changeVolume(Number(e.target.value) / 100)}
              aria-label="Volume"
            />
          ) : null}
        </div>
      ) : null}

      <div className="jol-vid-footer">
        <span className="jol-vid-countdown">
          {remaining !== null ? formatRemaining(remaining) : ""}
        </span>
        {endCondition === "skippable" || phase === "error" ? (
          <button
            type="button"
            className="jol-btn jol-btn--primary"
            disabled={submitted}
            onClick={() => complete(phase === "error" ? "load_error" : "skipped")}
          >
            {submitted ? "Continuing..." : "Continue"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
